import './media.informal-caregiver.screen.scss';
import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CardContent,
  CircularProgress,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import {LoadingButton} from '@mui/lab';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import RefreshIcon from '@mui/icons-material/Refresh';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../../../i18n/translations';
import {ContentPage} from '../../../../../components/content-page/content-page';
import {Modal} from '../../../../../components/modal/modal';
import FlatInput from '../../../../../components/flat-input/flat-input';
import DeleteResourceConfirmationModal from '../../../../../components/delete-resource-confirmation-modal';
import GenericHeader from '../../../../../components/generic-header/generic-header';
import {LoadingContent} from '../../../../../components/loading-content/loading-content';
import {RouterPrompt} from '../../../../../components/router-prompt/router-prompt';
import {AuthContext} from '../../../../../contexts/auth/context';
import {useSnackBar} from '../../../../../contexts/snack-bar/context';
import {MediaAlbum} from '../../../../../backend/enums/media-album';
import {MediaType} from '../../../../../backend/enums/media-type';
import {ProcessStatus} from '../../../../../backend/enums/process-status';
import {StorageFileType} from '../../../../../backend/enums/storage-file-type';
import {Media} from '../../../../../backend/interfaces/media';
import {ScreenPaths} from '../../../../../enums/screen-path';
import {MediaRepository} from '../../../../../repositories/media';
import {DataCacheManager} from '../../../../../managers/data-cache';
import {UsersRepository} from '../../../../../repositories/users';
import {FamilyMediaCard, FamilyMediaCardLabels} from './family-media-card';
import MediaExportActions from './media-export-actions';
import MediaCamera from './media-camera';

const PAGE_SIZE = 20;
const CAPTION_MAX_LENGTH = 250;

type PendingCancelNavigation = {
  path: string;
  seniorId: string;
  mediaId: string;
  generation: number;
  action: 'push' | 'replace';
};
type UploadStage = 'request' | 'transfer' | 'record';
type UploadFile = {
  file: File;
  fileType:
    | StorageFileType.JPG
    | StorageFileType.PNG
    | StorageFileType.GIF
    | StorageFileType.MP4
    | StorageFileType.MOV
    | StorageFileType.WebMVideo;
  mediaType: MediaType.Photo | MediaType.Video;
};

const galleryPathFor = (seniorId: string): string =>
  `${ScreenPaths.Office}${ScreenPaths.Office_InformalCaregiver}/${seniorId}${ScreenPaths.InformalCaregiver_Medias}`;

const validSharedMedia = (media: Media, seniorId: string): boolean =>
  media.seniorUserId === seniorId &&
  media.album === MediaAlbum.Shared &&
  (media.type === MediaType.Photo || media.type === MediaType.Video) &&
  typeof media.id === 'string' &&
  media.id.length > 0;

const visibleSharedRows = (rows: Array<Media>, seniorId: string): Array<Media> => {
  const seenIds = new Set<string>();
  return rows.filter((media) => {
    if (!validSharedMedia(media, seniorId) || seenIds.has(media.id)) return false;
    seenIds.add(media.id);
    return true;
  });
};

const identifyUploadFile = (file: File): UploadFile | undefined => {
  const extension = file.name.split('.').pop()?.toLowerCase();
  const types: Record<
    string,
    {fileType: UploadFile['fileType']; mediaType: UploadFile['mediaType']; mime: string}
  > = {
    jpg: {fileType: StorageFileType.JPG, mediaType: MediaType.Photo, mime: 'image/jpeg'},
    jpeg: {fileType: StorageFileType.JPG, mediaType: MediaType.Photo, mime: 'image/jpeg'},
    png: {fileType: StorageFileType.PNG, mediaType: MediaType.Photo, mime: 'image/png'},
    gif: {fileType: StorageFileType.GIF, mediaType: MediaType.Photo, mime: 'image/gif'},
    mp4: {fileType: StorageFileType.MP4, mediaType: MediaType.Video, mime: 'video/mp4'},
    mov: {fileType: StorageFileType.MOV, mediaType: MediaType.Video, mime: 'video/quicktime'},
  };
  const match = extension && types[extension];
  if (!match || (file.type && file.type.toLowerCase() !== match.mime)) return undefined;
  return {file, fileType: match.fileType, mediaType: match.mediaType};
};

const formatDate = (value?: Date | string): string => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString(undefined, {hourCycle: 'h23'});
};

export default function MediaInformalCaregiverOfficeScreen() {
  const {t} = useTranslation();
  const authContext = useContext(AuthContext);
  const snackBarContext = useSnackBar();
  const history = useHistory();
  const {seniorId, mediaId} = useParams<{seniorId?: string; mediaId?: string}>();
  const selectedSenior = authContext.state.session?.selectedRelationship?.withUser;
  const selectedSeniorId = selectedSenior?.id;
  const isSelectedSenior = !!seniorId && selectedSeniorId === seniorId;
  const currentUserId = authContext.state.session?.currentUser.id || '';
  const currentUserName = authContext.state.session?.currentUser.name || '';
  const isDetailRoute = !!mediaId;

  const [items, setItems] = useState<Array<Media>>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [nextServerSkip, setNextServerSkip] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [refreshError, setRefreshError] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(false);
  const [hasLoadedPage, setHasLoadedPage] = useState(false);
  const [ownerNames, setOwnerNames] = useState<Record<string, string>>({});
  const [signedUrlErrorIds, setSignedUrlErrorIds] = useState<Array<string>>([]);

  const [detailMedia, setDetailMedia] = useState<Media>();
  const detailDraftIdentityRef = useRef<{seniorId: string; mediaId: string}>();
  const detailMediaRef = useRef<Media>();
  detailMediaRef.current = detailMedia;
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailLoadError, setDetailLoadError] = useState(false);
  const [detailRefreshError, setDetailRefreshError] = useState(false);
  const [detailUnavailable, setDetailUnavailable] = useState(false);
  const [detailSignedUrlError, setDetailSignedUrlError] = useState(false);

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<UploadFile>();
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadStage, setUploadStage] = useState<UploadStage>();
  const [uploadPreviewUrl, setUploadPreviewUrl] = useState<string>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editDescription, setEditDescription] = useState('');
  const [pendingCancelNavigation, setPendingCancelNavigation] = useState<PendingCancelNavigation>();
  const [isSavingDescription, setIsSavingDescription] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Media>();
  const [isDeleting, setIsDeleting] = useState(false);

  const generationRef = useRef(0);
  const mountedRef = useRef(false);
  const routeMediaIdRef = useRef(mediaId);
  routeMediaIdRef.current = mediaId;
  const listRequestVersionRef = useRef(0);
  const listRequestControllerRef = useRef<AbortController>();
  const detailRequestVersionRef = useRef(0);
  const detailRequestControllerRef = useRef<AbortController>();
  const signedUrlControllersRef = useRef<Map<string, AbortController>>(new Map());
  const signedUrlAttemptsRef = useRef<Set<string>>(new Set());
  const detailSignedUrlAttemptRef = useRef(false);
  const uploadLockRef = useRef<string>();
  const editLocksRef = useRef<Set<string>>(new Set());
  const deleteLocksRef = useRef<Set<string>>(new Set());
  const ownerLookupInFlightRef = useRef<Set<string>>(new Set());
  const hasLoadedPageRef = useRef(false);
  hasLoadedPageRef.current = hasLoadedPage;
  const currentGeneration = generationRef.current;

  const labels: FamilyMediaCardLabels = {
    photo: t(translations.family_media.photo),
    video: t(translations.family_media.video),
    processing: t(translations.family_media.processing),
    processingFailed: t(translations.family_media.processing_failed),
    uploadedBy: t(translations.family_media.uploaded_by),
    signedUrlError: t(translations.family_media.signed_url_error),
    retry: t(translations.family_media.retry),
  };

  useLayoutEffect(() => {
    mountedRef.current = true;
    const signedUrlControllers = signedUrlControllersRef.current;
    return () => {
      mountedRef.current = false;
      generationRef.current += 1;
      listRequestVersionRef.current += 1;
      detailRequestVersionRef.current += 1;
      listRequestControllerRef.current?.abort();
      detailRequestControllerRef.current?.abort();
      signedUrlControllers.forEach((controller) => controller.abort());
      signedUrlControllers.clear();
    };
  }, []);

  // The parent only mounts a matching route and keys this screen by selected senior.
  const isCurrentSenior = useCallback(
    (targetSeniorId: string, generation: number): boolean =>
      mountedRef.current &&
      generationRef.current === generation &&
      seniorId === targetSeniorId &&
      selectedSeniorId === targetSeniorId,
    [selectedSeniorId, seniorId]
  );

  const isCurrentDetail = useCallback(
    (targetSeniorId: string, targetMediaId: string, generation: number): boolean =>
      isCurrentSenior(targetSeniorId, generation) && routeMediaIdRef.current === targetMediaId,
    [isCurrentSenior]
  );

  const showToast = useCallback(
    (severity: 'success' | 'error', message: string) => {
      void snackBarContext.setOfficeToastNotification({severity, message});
    },
    [snackBarContext]
  );

  const seniorLabelFor = (targetSeniorId: string): string =>
    selectedSenior?.id === targetSeniorId && selectedSenior.name
      ? selectedSenior.name
      : targetSeniorId;

  const refreshFirstPage = useCallback(
    async (targetSeniorId: string, generation: number, preserveLoaded: boolean): Promise<void> => {
      if (!isCurrentSenior(targetSeniorId, generation)) return;

      listRequestVersionRef.current += 1;
      const requestVersion = listRequestVersionRef.current;
      listRequestControllerRef.current?.abort();
      signedUrlControllersRef.current.forEach((signedUrlController) => signedUrlController.abort());
      signedUrlControllersRef.current.clear();
      const controller = new AbortController();
      listRequestControllerRef.current = controller;
      const hasLoadedPage = hasLoadedPageRef.current;

      if (preserveLoaded && hasLoadedPage) setIsRefreshing(true);
      else setIsLoading(true);
      setIsLoadingMore(false);
      setLoadError(false);
      setRefreshError(false);
      setLoadMoreError(false);

      try {
        const result = await MediaRepository.instance.get(
          targetSeniorId,
          PAGE_SIZE,
          0,
          controller.signal
        );
        if (
          controller.signal.aborted ||
          requestVersion !== listRequestVersionRef.current ||
          !isCurrentSenior(targetSeniorId, generation)
        ) {
          return;
        }

        const rows = Array.isArray(result.data) ? result.data : [];
        const rawCount = Number.isFinite(result.rawCount) ? result.rawCount : rows.length;
        const responseTotal = Number.isFinite(result.totalCount) ? result.totalCount : 0;
        setItems(visibleSharedRows(rows, targetSeniorId));
        setTotalCount(responseTotal);
        setNextServerSkip(rawCount);
        hasLoadedPageRef.current = true;
        setHasLoadedPage(true);
      } catch {
        if (
          controller.signal.aborted ||
          requestVersion !== listRequestVersionRef.current ||
          !isCurrentSenior(targetSeniorId, generation)
        ) {
          return;
        }
        if (preserveLoaded && hasLoadedPage) setRefreshError(true);
        else setLoadError(true);
      } finally {
        if (
          requestVersion === listRequestVersionRef.current &&
          isCurrentSenior(targetSeniorId, generation)
        ) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
      }
    },
    [isCurrentSenior]
  );

  useEffect(() => {
    if (!seniorId || !isSelectedSenior) return undefined;
    const generation = generationRef.current;
    void refreshFirstPage(seniorId, generation, false);
    return () => {
      listRequestVersionRef.current += 1;
      listRequestControllerRef.current?.abort();
    };
  }, [isSelectedSenior, refreshFirstPage, selectedSeniorId, seniorId]);

  const handleLoadMore = async () => {
    if (
      !seniorId ||
      !isSelectedSenior ||
      isLoadingMore ||
      nextServerSkip >= totalCount ||
      !hasLoadedPage
    ) {
      return;
    }

    const targetSeniorId = seniorId;
    const generation = generationRef.current;
    if (!isCurrentSenior(targetSeniorId, generation)) return;

    listRequestVersionRef.current += 1;
    const requestVersion = listRequestVersionRef.current;
    listRequestControllerRef.current?.abort();
    const controller = new AbortController();
    listRequestControllerRef.current = controller;
    const requestSkip = nextServerSkip;
    setIsLoadingMore(true);
    setLoadMoreError(false);

    try {
      const result = await MediaRepository.instance.get(
        targetSeniorId,
        PAGE_SIZE,
        requestSkip,
        controller.signal
      );
      if (
        controller.signal.aborted ||
        requestVersion !== listRequestVersionRef.current ||
        !isCurrentSenior(targetSeniorId, generation)
      ) {
        return;
      }

      const rawRows = Array.isArray(result.data) ? result.data : [];
      const pageItems = visibleSharedRows(rawRows, targetSeniorId);
      setItems((current) => {
        const existingIds = new Set(current.map((item) => item.id));
        return [...current, ...pageItems.filter((item) => !existingIds.has(item.id))];
      });
      setTotalCount(Number.isFinite(result.totalCount) ? result.totalCount : 0);
      setNextServerSkip(
        requestSkip + (Number.isFinite(result.rawCount) ? result.rawCount : rawRows.length)
      );
    } catch {
      if (
        controller.signal.aborted ||
        requestVersion !== listRequestVersionRef.current ||
        !isCurrentSenior(targetSeniorId, generation)
      ) {
        return;
      }
      setLoadMoreError(true);
    } finally {
      if (
        requestVersion === listRequestVersionRef.current &&
        isCurrentSenior(targetSeniorId, generation)
      ) {
        setIsLoadingMore(false);
      }
    }
  };

  const refreshList = () => {
    if (seniorId && isSelectedSenior) {
      void refreshFirstPage(seniorId, generationRef.current, true);
    }
  };

  const invalidatePendingMediaReads = () => {
    listRequestVersionRef.current += 1;
    detailRequestVersionRef.current += 1;
    listRequestControllerRef.current?.abort();
    detailRequestControllerRef.current?.abort();
    signedUrlControllersRef.current.forEach((controller) => controller.abort());
    signedUrlControllersRef.current.clear();
    setIsLoading(false);
    setIsRefreshing(false);
    setIsLoadingMore(false);
    setLoadMoreError(false);
    setDetailLoading(false);
  };

  const applyFreshMedia = useCallback(
    (freshMedia: Media, targetSeniorId: string, generation: number) => {
      if (!isCurrentSenior(targetSeniorId, generation)) return;
      if (!validSharedMedia(freshMedia, targetSeniorId)) {
        setItems((current) => current.filter((item) => item.id !== freshMedia.id));
        setDetailMedia((current) => (current?.id === freshMedia.id ? undefined : current));
        setDetailUnavailable(true);
        return;
      }
      setItems((current) => current.map((item) => (item.id === freshMedia.id ? freshMedia : item)));
      setDetailMedia((current) => (current?.id === freshMedia.id ? freshMedia : current));
      setSignedUrlErrorIds((current) => current.filter((id) => id !== freshMedia.id));
      setDetailSignedUrlError(false);
    },
    [isCurrentSenior]
  );

  const refreshSignedListUrl = async (
    itemId: string,
    targetSeniorId: string,
    generation: number
  ) => {
    if (!isCurrentSenior(targetSeniorId, generation)) return;
    if (signedUrlAttemptsRef.current.has(itemId)) {
      setSignedUrlErrorIds((current) =>
        current.includes(itemId) ? current : [...current, itemId]
      );
      return;
    }
    signedUrlAttemptsRef.current.add(itemId);

    const controller = new AbortController();
    signedUrlControllersRef.current.set(itemId, controller);
    try {
      const freshMedia = await MediaRepository.instance.getById(itemId, controller.signal);
      if (controller.signal.aborted || !isCurrentSenior(targetSeniorId, generation)) return;
      if (freshMedia.id !== itemId || !validSharedMedia(freshMedia, targetSeniorId)) {
        setItems((current) => current.filter((item) => item.id !== itemId));
        return;
      }
      applyFreshMedia(freshMedia, targetSeniorId, generation);
    } catch {
      if (controller.signal.aborted || !isCurrentSenior(targetSeniorId, generation)) return;
      setSignedUrlErrorIds((current) =>
        current.includes(itemId) ? current : [...current, itemId]
      );
    } finally {
      if (signedUrlControllersRef.current.get(itemId) === controller) {
        signedUrlControllersRef.current.delete(itemId);
      }
    }
  };

  const retrySignedListUrl = (itemId: string) => {
    if (!seniorId) return;
    signedUrlAttemptsRef.current.delete(itemId);
    setSignedUrlErrorIds((current) => current.filter((id) => id !== itemId));
    void refreshSignedListUrl(itemId, seniorId, generationRef.current);
  };

  const fetchDetail = useCallback(
    async (
      targetSeniorId: string,
      targetMediaId: string,
      generation: number,
      mode: 'initial' | 'refresh' | 'signed'
    ): Promise<void> => {
      if (!isCurrentDetail(targetSeniorId, targetMediaId, generation)) return;

      detailRequestVersionRef.current += 1;
      const requestVersion = detailRequestVersionRef.current;
      detailRequestControllerRef.current?.abort();
      const controller = new AbortController();
      detailRequestControllerRef.current = controller;
      const hasMedia = detailMediaRef.current?.id === targetMediaId;

      if (mode === 'initial') {
        detailSignedUrlAttemptRef.current = false;
        setDetailMedia(undefined);
        setDetailLoading(true);
        setDetailLoadError(false);
        setDetailUnavailable(false);
        setDetailRefreshError(false);
        setDetailSignedUrlError(false);
      } else if (mode === 'refresh') {
        detailSignedUrlAttemptRef.current = false;
        setDetailRefreshError(false);
        setDetailUnavailable(false);
      } else {
        setDetailSignedUrlError(false);
      }

      try {
        const freshMedia = await MediaRepository.instance.getById(targetMediaId, controller.signal);
        if (
          controller.signal.aborted ||
          requestVersion !== detailRequestVersionRef.current ||
          !isCurrentDetail(targetSeniorId, targetMediaId, generation)
        ) {
          return;
        }

        if (freshMedia.id !== targetMediaId || !validSharedMedia(freshMedia, targetSeniorId)) {
          setDetailMedia(undefined);
          setDetailUnavailable(true);
          return;
        }
        setDetailMedia(freshMedia);
        if (
          detailDraftIdentityRef.current?.seniorId !== targetSeniorId ||
          detailDraftIdentityRef.current?.mediaId !== targetMediaId
        ) {
          detailDraftIdentityRef.current = {seniorId: targetSeniorId, mediaId: targetMediaId};
          setEditDescription(freshMedia.description || '');
        }
        setDetailLoadError(false);
        setDetailRefreshError(false);
        setDetailUnavailable(false);
        setDetailSignedUrlError(false);
        setItems((current) =>
          current.map((item) => (item.id === freshMedia.id ? freshMedia : item))
        );
      } catch {
        if (
          controller.signal.aborted ||
          requestVersion !== detailRequestVersionRef.current ||
          !isCurrentDetail(targetSeniorId, targetMediaId, generation)
        ) {
          return;
        }
        if (mode === 'initial') setDetailLoadError(true);
        else if (mode === 'refresh') setDetailRefreshError(true);
        else setDetailSignedUrlError(true);
      } finally {
        if (
          requestVersion === detailRequestVersionRef.current &&
          isCurrentDetail(targetSeniorId, targetMediaId, generation)
        ) {
          setDetailLoading(false);
          if (mode === 'signed' && !hasMedia && !detailMediaRef.current) setDetailLoadError(true);
        }
      }
    },
    [isCurrentDetail]
  );

  useEffect(() => {
    detailDraftIdentityRef.current = undefined;
    setEditDescription('');
    setPendingCancelNavigation(undefined);
    setDeleteTarget(undefined);
    setIsSavingDescription(false);
    setIsDeleting(false);

    if (!seniorId || !mediaId || !isSelectedSenior) {
      setDetailMedia(undefined);
      setDetailLoading(false);
      setDetailLoadError(false);
      setDetailRefreshError(false);
      setDetailUnavailable(false);
      setDetailSignedUrlError(false);
      return undefined;
    }

    const generation = generationRef.current;
    void fetchDetail(seniorId, mediaId, generation, 'initial');
    return () => {
      detailRequestVersionRef.current += 1;
      detailRequestControllerRef.current?.abort();
    };
  }, [fetchDetail, isSelectedSenior, mediaId, seniorId]);

  const handleDetailImageError = () => {
    if (
      !seniorId ||
      !mediaId ||
      !isSelectedSenior ||
      !detailMedia ||
      isSavingDescription ||
      isDeleting
    ) {
      return;
    }
    if (detailSignedUrlAttemptRef.current) {
      setDetailSignedUrlError(true);
      return;
    }
    detailSignedUrlAttemptRef.current = true;
    void fetchDetail(seniorId, mediaId, generationRef.current, 'signed');
  };

  const ownerIdsKey = useMemo(() => {
    const ids = new Set(items.map((item) => item.ownerUserId));
    if (detailMedia?.ownerUserId) ids.add(detailMedia.ownerUserId);
    if (currentUserId) ids.delete(currentUserId);
    return Array.from(ids).sort().join('|');
  }, [currentUserId, detailMedia?.ownerUserId, items]);

  useEffect(() => {
    if (!ownerIdsKey) return;
    const targetSeniorId = seniorId;
    const generation = generationRef.current;
    const ownerIds = ownerIdsKey.split('|').filter(Boolean);

    ownerIds.forEach((ownerId) => {
      const cached = DataCacheManager.instance.users[ownerId];
      if (cached?.name) {
        setOwnerNames((current) => ({...current, [ownerId]: cached.name}));
        return;
      }
      if (ownerLookupInFlightRef.current.has(ownerId)) return;
      ownerLookupInFlightRef.current.add(ownerId);
      void UsersRepository.instance
        .getUserPublicInfo(ownerId)
        .then((userInfo) => {
          if (userInfo?.name) {
            DataCacheManager.instance.addUser(ownerId, userInfo);
            if (targetSeniorId && isCurrentSenior(targetSeniorId, generation)) {
              setOwnerNames((current) => ({...current, [ownerId]: userInfo.name}));
            }
          }
        })
        .catch(() => {})
        .finally(() => ownerLookupInFlightRef.current.delete(ownerId));
    });
  }, [currentGeneration, isCurrentSenior, ownerIdsKey, seniorId]);

  const getOwnerName = (media: Media): string => {
    if (media.ownerUserId === currentUserId)
      return currentUserName || t(translations.family_media.you);
    return ownerNames[media.ownerUserId] || media.ownerUserId;
  };

  const beginUpload = () => {
    if (!seniorId || !isSelectedSenior || isDetailRoute) return;
    setUploadPreviewUrl(undefined);
    setUploadFile(undefined);
    setUploadDescription('');
    setCameraOpen(false);
    setUploadDialogOpen(true);
  };

  useEffect(() => {
    if (!uploadFile) {
      setUploadPreviewUrl(undefined);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(uploadFile.file);
    setUploadPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [uploadFile]);

  const handleChooseFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    if (!file) return;
    const identified = identifyUploadFile(file);
    if (!identified) {
      setUploadPreviewUrl(undefined);
      setUploadFile(undefined);
      showToast('error', t(translations.family_media.invalid_file_type));
      return;
    }
    setUploadPreviewUrl(undefined);
    setUploadFile(identified);
    setUploadDescription('');
  };

  const closeUploadDialog = () => {
    if (uploadStage) return;
    setUploadDialogOpen(false);
    setCameraOpen(false);
    setUploadPreviewUrl(undefined);
    setUploadFile(undefined);
    setUploadDescription('');
  };

  const handleUpload = async () => {
    if (!seniorId || !isSelectedSenior || !uploadFile || !currentUserId) return;
    const targetSeniorId = seniorId;
    const generation = generationRef.current;
    const capturedSeniorLabel = seniorLabelFor(targetSeniorId);
    if (!isCurrentSenior(targetSeniorId, generation)) return;
    const lockKey = `${targetSeniorId}:${generation}`;
    if (uploadLockRef.current === lockKey) return;
    uploadLockRef.current = lockKey;
    const description = uploadDescription.trim();
    let stage: UploadStage = 'request';
    let linkingUrl: string | undefined;
    setUploadStage(stage);

    try {
      const storageRequest = await MediaRepository.instance.requestMediaUpload(
        targetSeniorId,
        uploadFile.mediaType,
        uploadFile.fileType
      );
      stage = 'transfer';
      if (isCurrentSenior(targetSeniorId, generation)) setUploadStage(stage);
      linkingUrl = await MediaRepository.instance.uploadMedia(storageRequest, uploadFile.file);
      stage = 'record';
      if (isCurrentSenior(targetSeniorId, generation)) setUploadStage(stage);

      const savedMedia = await MediaRepository.instance.createMedia(
        targetSeniorId,
        uploadFile.mediaType,
        linkingUrl,
        description || undefined
      );
      if (
        !savedMedia?.id ||
        savedMedia.seniorUserId !== targetSeniorId ||
        savedMedia.ownerUserId !== currentUserId ||
        savedMedia.album !== MediaAlbum.Shared ||
        savedMedia.type !== uploadFile.mediaType
      ) {
        throw new Error(
          'The created media response did not match the requested senior/shared media.'
        );
      }

      if (isCurrentSenior(targetSeniorId, generation)) {
        setItems((current) => [savedMedia, ...current.filter((item) => item.id !== savedMedia.id)]);
        hasLoadedPageRef.current = true;
        setHasLoadedPage(true);
        setTotalCount((current) => current + 1);
        setUploadDialogOpen(false);
        setUploadFile(undefined);
        setUploadDescription('');
        setUploadStage(undefined);
        void refreshFirstPage(targetSeniorId, generation, true);
      }
      showToast(
        'success',
        `${t(translations.family_media.upload_success)} — ${capturedSeniorLabel}`
      );
    } catch {
      if (stage === 'record' && linkingUrl) {
        // The list response contains signed display URLs, not the canonical linking URL. Refresh the
        // page for the user to inspect before retrying; never replay an ambiguous create request.
        if (isCurrentSenior(targetSeniorId, generation)) {
          const hasLoadedPage = hasLoadedPageRef.current;
          listRequestVersionRef.current += 1;
          const verificationVersion = listRequestVersionRef.current;
          listRequestControllerRef.current?.abort();
          signedUrlControllersRef.current.forEach((controller) => controller.abort());
          signedUrlControllersRef.current.clear();
          const verificationController = new AbortController();
          listRequestControllerRef.current = verificationController;
          if (hasLoadedPage) setIsRefreshing(true);
          else setIsLoading(true);
          setIsLoadingMore(false);
          setLoadError(false);
          setRefreshError(false);
          setLoadMoreError(false);

          try {
            const verification = await MediaRepository.instance.get(
              targetSeniorId,
              PAGE_SIZE,
              0,
              verificationController.signal
            );
            const canApplyVerification =
              !verificationController.signal.aborted &&
              verificationVersion === listRequestVersionRef.current &&
              isCurrentSenior(targetSeniorId, generation);
            if (canApplyVerification) {
              const verifiedRows = visibleSharedRows(verification.data || [], targetSeniorId);
              setItems(verifiedRows);
              setTotalCount(Number.isFinite(verification.totalCount) ? verification.totalCount : 0);
              setNextServerSkip(
                Number.isFinite(verification.rawCount)
                  ? verification.rawCount
                  : verification.data.length
              );
              hasLoadedPageRef.current = true;
              setHasLoadedPage(true);
            }
          } catch {
            if (
              !verificationController.signal.aborted &&
              verificationVersion === listRequestVersionRef.current &&
              isCurrentSenior(targetSeniorId, generation)
            ) {
              if (hasLoadedPage) setRefreshError(true);
              else setLoadError(true);
              // Keep the loaded list and show the verification failure separately from the upload result.
            }
          } finally {
            if (
              verificationVersion === listRequestVersionRef.current &&
              isCurrentSenior(targetSeniorId, generation)
            ) {
              setIsLoading(false);
              setIsRefreshing(false);
            }
          }

          if (isCurrentSenior(targetSeniorId, generation)) {
            setUploadDialogOpen(false);
            setUploadFile(undefined);
            setUploadDescription('');
            setUploadStage(undefined);
          }
        }
        if (isCurrentSenior(targetSeniorId, generation)) {
          showToast('error', t(translations.family_media.upload_record_error));
        }
      } else if (isCurrentSenior(targetSeniorId, generation)) {
        const errorKey =
          stage === 'request'
            ? 'upload_request_error'
            : stage === 'transfer'
              ? 'upload_transfer_error'
              : 'upload_record_error';
        showToast('error', t(translations.family_media[errorKey]));
      }
    } finally {
      if (uploadLockRef.current === lockKey) uploadLockRef.current = undefined;
      if (isCurrentSenior(targetSeniorId, generation)) setUploadStage(undefined);
    }
  };

  const handleCancelDetail = () => {
    const target = detailMedia;
    if (
      !seniorId ||
      !mediaId ||
      !target ||
      !isSelectedSenior ||
      !isCurrentSenior(seniorId, generationRef.current) ||
      target.id !== mediaId ||
      !validSharedMedia(target, seniorId) ||
      target.ownerUserId !== currentUserId
    ) {
      return;
    }

    setEditDescription(target.description || '');
    setPendingCancelNavigation({
      path: galleryPathFor(seniorId),
      seniorId,
      mediaId,
      generation: generationRef.current,
      action: 'push',
    });
  };

  const handleSaveDescription = async () => {
    const target = detailMedia;
    if (
      !seniorId ||
      !mediaId ||
      !target ||
      !isSelectedSenior ||
      target.id !== mediaId ||
      !validSharedMedia(target, seniorId) ||
      target.ownerUserId !== currentUserId ||
      editDescription.length > CAPTION_MAX_LENGTH ||
      editDescription.trim() === (target.description || '').trim()
    ) {
      return;
    }
    const targetSeniorId = seniorId;
    const generation = generationRef.current;
    const capturedSeniorLabel = seniorLabelFor(targetSeniorId);
    if (!isCurrentDetail(targetSeniorId, target.id, generation)) return;
    const lockKey = `${targetSeniorId}:${generation}:${target.id}`;
    if (editLocksRef.current.has(lockKey) || deleteLocksRef.current.has(lockKey)) return;
    editLocksRef.current.add(lockKey);
    const description = editDescription.trim();
    let requestSucceeded = false;
    setIsSavingDescription(true);

    try {
      const updated = await MediaRepository.instance.updateDescription(
        target.id,
        description || null
      );
      requestSucceeded = true;
      if (
        updated.id !== target.id ||
        !validSharedMedia(updated, targetSeniorId) ||
        updated.ownerUserId !== currentUserId
      ) {
        throw new Error('Updated media response did not match the captured shared senior item.');
      }

      const successMessage = `${t(translations.family_media.edit_success)} — ${capturedSeniorLabel}`;
      if (isCurrentDetail(targetSeniorId, target.id, generation)) {
        invalidatePendingMediaReads();
        const caption = updated.description || undefined;
        setItems((current) =>
          current.map((item) => (item.id === target.id ? {...item, description: caption} : item))
        );
        setDetailMedia((current) =>
          current?.id === target.id ? {...current, description: caption} : current
        );
        setEditDescription(caption || '');
        void refreshFirstPage(targetSeniorId, generation, true);
      }
      showToast('success', successMessage);
    } catch {
      if (isCurrentDetail(targetSeniorId, target.id, generation)) {
        showToast('error', t(translations.family_media.edit_error));
        if (requestSucceeded) {
          void refreshFirstPage(targetSeniorId, generation, true);
          void fetchDetail(targetSeniorId, target.id, generation, 'refresh');
        }
      }
    } finally {
      editLocksRef.current.delete(lockKey);
      if (isCurrentDetail(targetSeniorId, target.id, generation)) setIsSavingDescription(false);
    }
  };

  const handleDeleteMedia = async () => {
    const target = deleteTarget;
    if (
      !seniorId ||
      !target ||
      !isSelectedSenior ||
      target.id !== mediaId ||
      target.seniorUserId !== seniorId ||
      target.album !== MediaAlbum.Shared ||
      target.ownerUserId !== currentUserId
    ) {
      return;
    }
    const targetSeniorId = seniorId;
    const generation = generationRef.current;
    const capturedSeniorLabel = seniorLabelFor(targetSeniorId);
    if (!isCurrentDetail(targetSeniorId, target.id, generation)) return;
    const lockKey = `${targetSeniorId}:${generation}:${target.id}`;
    if (deleteLocksRef.current.has(lockKey) || editLocksRef.current.has(lockKey)) return;
    deleteLocksRef.current.add(lockKey);
    setIsDeleting(true);

    try {
      const status = await MediaRepository.instance.deleteById(target.id);
      if (status !== 204) throw new Error(`Media delete was not confirmed (status ${status}).`);
      if (isCurrentDetail(targetSeniorId, target.id, generation)) {
        invalidatePendingMediaReads();
        setItems((current) => current.filter((item) => item.id !== target.id));
        setTotalCount((current) => Math.max(0, current - 1));
        setDetailMedia((current) => (current?.id === target.id ? undefined : current));
        setEditDescription(target.description || '');
        setDeleteTarget(undefined);
        if (routeMediaIdRef.current === target.id) {
          setPendingCancelNavigation({
            path: galleryPathFor(targetSeniorId),
            seniorId: targetSeniorId,
            mediaId: target.id,
            generation,
            action: 'replace',
          });
        }
        void refreshFirstPage(targetSeniorId, generation, true);
      }
      showToast(
        'success',
        `${t(translations.family_media.delete_success)} — ${capturedSeniorLabel}`
      );
    } catch {
      if (isCurrentDetail(targetSeniorId, target.id, generation)) {
        showToast('error', t(translations.family_media.delete_error));
      }
    } finally {
      deleteLocksRef.current.delete(lockKey);
      if (isCurrentDetail(targetSeniorId, target.id, generation)) setIsDeleting(false);
    }
  };

  const hasCurrentData = hasLoadedPage;
  const galleryItems = hasCurrentData ? items : [];
  const hasCurrentLoadError = loadError && !hasCurrentData;
  const visibleDeleteTarget =
    deleteTarget &&
    deleteTarget.id === mediaId &&
    deleteTarget.seniorUserId === seniorId &&
    deleteTarget.ownerUserId === currentUserId &&
    isSelectedSenior
      ? deleteTarget
      : undefined;
  const validDetail =
    detailMedia &&
    mediaId === detailMedia.id &&
    seniorId === detailMedia.seniorUserId &&
    detailMedia.album === MediaAlbum.Shared
      ? detailMedia
      : undefined;
  const canEditDetail =
    !!validDetail && isSelectedSenior && validDetail.ownerUserId === currentUserId;
  const isCaptionChanged =
    canEditDetail && editDescription.trim() !== (validDetail?.description || '').trim();

  useEffect(() => {
    const pendingNavigation = pendingCancelNavigation;
    if (!pendingNavigation || isCaptionChanged) return;

    setPendingCancelNavigation(undefined);
    if (
      isCurrentSenior(pendingNavigation.seniorId, pendingNavigation.generation) &&
      routeMediaIdRef.current === pendingNavigation.mediaId
    ) {
      if (pendingNavigation.action === 'replace') history.replace(pendingNavigation.path);
      else history.push(pendingNavigation.path);
    }
  }, [history, isCaptionChanged, isCurrentSenior, pendingCancelNavigation]);

  const pageHeader = (
    <GenericHeader
      isLoading={isDetailRoute ? detailLoading && !validDetail : isLoading && !hasCurrentData}
      showBackButton={isDetailRoute}
      pageTitle={isDetailRoute ? undefined : t(translations.family_media.title)}
      titleCrumbs={
        isDetailRoute && seniorId
          ? [
              {label: t(translations.family_media.title), link: galleryPathFor(seniorId)},
              {
                label:
                  validDetail?.description ||
                  (validDetail?.type === MediaType.Video
                    ? t(translations.family_media.video)
                    : t(translations.family_media.photo)),
                link: history.location.pathname,
              },
            ]
          : undefined
      }
      leftColumn={<Box />}
      rightColumn={
        isSelectedSenior && !isDetailRoute ? (
          <Stack direction="row" spacing={1} sx={{color: 'common.white'}}>
            <Button
              color="inherit"
              startIcon={<RefreshIcon />}
              disabled={isRefreshing || isLoading}
              onClick={refreshList}
            >
              {t(translations.family_media.refresh)}
            </Button>
            <Button
              variant="inverted"
              startIcon={<AddPhotoAlternateOutlinedIcon />}
              disabled={!currentUserId || !!uploadStage}
              onClick={beginUpload}
            >
              {t(translations.family_media.upload)}
            </Button>
          </Stack>
        ) : isSelectedSenior &&
          seniorId &&
          validDetail?.url &&
          validDetail.processingStatus !== ProcessStatus.InProgress &&
          validDetail.processingStatus !== ProcessStatus.Failed ? (
          <MediaExportActions
            key={`${seniorId}:${validDetail.id}`}
            media={validDetail}
            seniorId={seniorId}
            isCurrent={() => isCurrentDetail(seniorId, validDetail.id, currentGeneration)}
          />
        ) : undefined
      }
    />
  );

  return (
    <ContentPage fullWidth>
      <Box className="family-media-page">
        {pageHeader}
        <Box
          className="family-media-page-content"
          sx={
            isDetailRoute
              ? {bgcolor: 'background.paper', borderRadius: 1, p: {xs: 2, sm: 3}}
              : undefined
          }
        >
          {!isSelectedSenior ? (
            <Alert severity="info">{t(translations.family_media.senior_unavailable)}</Alert>
          ) : isDetailRoute ? (
            <>
              {detailLoadError && !validDetail ? (
                <Alert
                  severity="error"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() =>
                        seniorId &&
                        mediaId &&
                        void fetchDetail(seniorId, mediaId, generationRef.current, 'refresh')
                      }
                    >
                      {t(translations.family_media.retry)}
                    </Button>
                  }
                >
                  {t(translations.family_media.detail_load_error)}
                </Alert>
              ) : detailUnavailable ? (
                <Alert severity="info">{t(translations.family_media.detail_unavailable)}</Alert>
              ) : detailLoading && !validDetail ? (
                <LoadingContent className="family-media-detail-loading" />
              ) : validDetail ? (
                <Stack spacing={2} className="family-media-detail">
                  {detailRefreshError && (
                    <Alert
                      severity="error"
                      action={
                        <Button
                          color="inherit"
                          size="small"
                          disabled={isSavingDescription || isDeleting}
                          onClick={() =>
                            seniorId &&
                            mediaId &&
                            void fetchDetail(seniorId, mediaId, generationRef.current, 'refresh')
                          }
                        >
                          {t(translations.family_media.retry)}
                        </Button>
                      }
                    >
                      {t(translations.family_media.refresh_error)}
                    </Alert>
                  )}
                  {detailSignedUrlError && (
                    <Alert
                      severity="error"
                      action={
                        <Button
                          color="inherit"
                          size="small"
                          disabled={isSavingDescription || isDeleting}
                          onClick={() => {
                            detailSignedUrlAttemptRef.current = false;
                            handleDetailImageError();
                          }}
                        >
                          {t(translations.family_media.retry)}
                        </Button>
                      }
                    >
                      {t(translations.family_media.signed_url_error)}
                    </Alert>
                  )}
                  {validDetail.processingStatus === ProcessStatus.InProgress && (
                    <Alert
                      severity="info"
                      action={
                        <Button
                          color="inherit"
                          size="small"
                          disabled={isSavingDescription || isDeleting}
                          onClick={() =>
                            seniorId &&
                            mediaId &&
                            void fetchDetail(seniorId, mediaId, generationRef.current, 'refresh')
                          }
                        >
                          {t(translations.family_media.refresh)}
                        </Button>
                      }
                    >
                      {t(translations.family_media.processing)}
                    </Alert>
                  )}
                  {validDetail.processingStatus === ProcessStatus.Failed && (
                    <Alert severity="warning">
                      {t(translations.family_media.processing_failed)}
                    </Alert>
                  )}
                  <Box className="family-media-detail-card">
                    {validDetail.type === MediaType.Photo ? (
                      <img
                        className="family-media-detail-image"
                        src={validDetail.url}
                        alt={validDetail.description || ''}
                        onError={handleDetailImageError}
                      />
                    ) : (
                      <video
                        className="family-media-detail-video"
                        controls
                        playsInline
                        preload="metadata"
                        poster={validDetail.thumbnailUrl || undefined}
                        src={validDetail.url}
                        onError={handleDetailImageError}
                      />
                    )}
                    <CardContent>
                      {canEditDetail ? (
                        <FlatInput
                          label={t(translations.family_media.description)}
                          value={editDescription}
                          onChange={(event) => setEditDescription(event.target.value)}
                          disabled={isSavingDescription || isDeleting}
                          multiline
                          rows={4}
                          inputProps={{maxLength: CAPTION_MAX_LENGTH}}
                          helperText={`${editDescription.length}/${CAPTION_MAX_LENGTH}`}
                          FormHelperTextProps={{sx: {textAlign: 'right'}}}
                        />
                      ) : (
                        validDetail.description && (
                          <Typography variant="body1" sx={{whiteSpace: 'pre-wrap', mb: 1}}>
                            {validDetail.description}
                          </Typography>
                        )
                      )}
                      {formatDate(validDetail.createdAt) && (
                        <Typography color="text.secondary" variant="body2" sx={{mb: 0.5}}>
                          {t(translations.family_media.created_at)}:{' '}
                          {formatDate(validDetail.createdAt)}
                        </Typography>
                      )}
                      <Typography color="text.secondary" variant="body2">
                        {t(translations.family_media.uploaded_by)}: {getOwnerName(validDetail)}
                      </Typography>
                    </CardContent>
                  </Box>
                  {canEditDetail && (
                    <Box className="family-media-detail-actions-container">
                      <Divider />
                      <Stack
                        className="family-media-detail-actions"
                        direction={{xs: 'column', sm: 'row'}}
                        alignItems={{xs: 'center', sm: 'stretch'}}
                        gap="10px"
                        justifyContent="space-between"
                      >
                        <Button
                          variant="text"
                          color="error"
                          type="button"
                          sx={{
                            width: '100%',
                            height: '40px',
                            padding: '8px 16px',
                            borderRadius: '5px',
                            maxWidth: '252px',
                          }}
                          disabled={isSavingDescription || isDeleting}
                          onClick={handleCancelDetail}
                        >
                          {t(translations.family_media.cancel)}
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          type="button"
                          endIcon={<DeleteForeverIcon />}
                          sx={{width: '100%', maxWidth: '252px'}}
                          disabled={isSavingDescription || isDeleting}
                          onClick={() => setDeleteTarget(validDetail)}
                        >
                          {t(translations.family_media.delete)}
                        </Button>
                        <LoadingButton
                          sx={{width: '100%', maxWidth: '252px'}}
                          type="button"
                          variant="contained"
                          loading={isSavingDescription}
                          loadingPosition={isSavingDescription ? 'start' : undefined}
                          disabled={
                            !isCaptionChanged ||
                            editDescription.length > CAPTION_MAX_LENGTH ||
                            isSavingDescription ||
                            isDeleting
                          }
                          onClick={handleSaveDescription}
                        >
                          {isSavingDescription
                            ? t(translations.family_media.saving)
                            : t(translations.family_media.save)}
                        </LoadingButton>
                      </Stack>
                    </Box>
                  )}
                </Stack>
              ) : detailLoadError ? (
                <Alert severity="error">{t(translations.family_media.detail_not_found)}</Alert>
              ) : (
                <LoadingContent className="family-media-detail-loading" />
              )}
            </>
          ) : hasCurrentLoadError ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={refreshList}>
                  {t(translations.family_media.retry)}
                </Button>
              }
            >
              {t(translations.family_media.load_error)}
            </Alert>
          ) : isLoading && !hasCurrentData ? (
            <LoadingContent />
          ) : (
            <Stack spacing={2}>
              {refreshError && (
                <Alert
                  severity="error"
                  action={
                    <Button color="inherit" size="small" onClick={refreshList}>
                      {t(translations.family_media.retry)}
                    </Button>
                  }
                >
                  {t(translations.family_media.refresh_error)}
                </Alert>
              )}
              {galleryItems.length === 0 ? (
                <Stack spacing={1}>
                  <Typography>{t(translations.family_media.empty_title)}</Typography>
                  <Typography color="text.secondary">
                    {t(translations.family_media.empty_description)}
                  </Typography>
                </Stack>
              ) : (
                <Box className="family-media-grid">
                  {galleryItems.map((item) => (
                    <FamilyMediaCard
                      key={item.id}
                      media={item}
                      ownerName={getOwnerName(item)}
                      labels={labels}
                      signedUrlError={signedUrlErrorIds.includes(item.id)}
                      onOpen={() => history.push(`${galleryPathFor(seniorId || '')}/${item.id}`)}
                      onImageError={() =>
                        seniorId &&
                        void refreshSignedListUrl(item.id, seniorId, generationRef.current)
                      }
                      onRetrySignedUrl={() => retrySignedListUrl(item.id)}
                    />
                  ))}
                </Box>
              )}
              {loadMoreError && (
                <Alert
                  severity="error"
                  action={
                    <Button color="inherit" size="small" onClick={handleLoadMore}>
                      {t(translations.family_media.retry)}
                    </Button>
                  }
                >
                  {t(translations.family_media.load_error)}
                </Alert>
              )}
              {nextServerSkip < totalCount && (
                <Box className="family-media-load-more">
                  <LoadingButton
                    variant="outlined"
                    loading={isLoadingMore}
                    loadingPosition="start"
                    startIcon={isLoadingMore ? <CircularProgress size={16} /> : <RefreshIcon />}
                    onClick={handleLoadMore}
                  >
                    {isLoadingMore
                      ? t(translations.family_media.loading_more)
                      : t(translations.family_media.load_more)}
                  </LoadingButton>
                </Box>
              )}
            </Stack>
          )}
        </Box>
      </Box>

      <Modal
        open={uploadDialogOpen}
        onClose={closeUploadDialog}
        title={t(translations.family_media.upload_photo)}
        fullWidth
        maxWidth="sm"
        actions={[
          {
            label: t(translations.family_media.cancel),
            type: 'destructive',
            disabled: !!uploadStage,
            onClick: closeUploadDialog,
          },
          {
            label: t(translations.family_media.upload),
            type: 'nondestructive',
            loading: !!uploadStage,
            disabled: !uploadFile || !currentUserId || !!uploadStage || cameraOpen,
            onClick: handleUpload,
          },
        ]}
      >
        <Stack spacing={2}>
          <input
            ref={fileInputRef}
            className="family-media-file-input"
            type="file"
            accept="image/jpeg,image/png,image/gif,video/mp4,video/quicktime,.jpg,.jpeg,.png,.gif,.mp4,.mov"
            onChange={handleChooseFile}
            disabled={!!uploadStage || cameraOpen}
          />
          <Button
            variant="outlined"
            startIcon={<AddPhotoAlternateOutlinedIcon />}
            sx={{bgcolor: '#eaf5fd'}}
            onClick={() => fileInputRef.current?.click()}
            disabled={!!uploadStage || cameraOpen}
          >
            {uploadFile ? uploadFile.file.name : t(translations.family_media.upload_photo)}
          </Button>
          {cameraOpen ? (
            <MediaCamera
              onCancel={() => setCameraOpen(false)}
              onCapture={(file, fileType, mediaType) => {
                setUploadFile({file, fileType, mediaType});
                setCameraOpen(false);
              }}
            />
          ) : (
            <Button
              variant="outlined"
              startIcon={<PhotoCameraOutlinedIcon />}
              sx={{bgcolor: '#eaf5fd'}}
              disabled={!!uploadStage}
              onClick={() => setCameraOpen(true)}
            >
              {t(translations.family_media.camera)}
            </Button>
          )}
          {!cameraOpen && uploadFile && uploadPreviewUrl && (
            <Box className="family-media-upload-preview">
              {uploadFile.mediaType === MediaType.Video ? (
                <Box
                  component="video"
                  src={uploadPreviewUrl}
                  controls
                  playsInline
                  sx={{maxWidth: '100%', maxHeight: 320}}
                />
              ) : (
                <img src={uploadPreviewUrl} alt={uploadFile.file.name} />
              )}
            </Box>
          )}
          <FlatInput
            label={t(translations.family_media.description)}
            value={uploadDescription}
            onChange={(event) =>
              setUploadDescription(event.target.value.slice(0, CAPTION_MAX_LENGTH))
            }
            multiline
            minRows={2}
            maxRows={5}
            inputProps={{maxLength: CAPTION_MAX_LENGTH}}
            counter={{current: uploadDescription.length, max: CAPTION_MAX_LENGTH}}
            disabled={!!uploadStage}
          />
          {uploadStage && (
            <Stack spacing={1}>
              <LinearProgress
                variant="determinate"
                value={uploadStage === 'request' ? 20 : uploadStage === 'transfer' ? 60 : 90}
              />
              <Typography variant="body2">
                {t(translations.family_media.upload_progress)}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Modal>

      {validDetail && canEditDetail && (
        <RouterPrompt
          when={isCaptionChanged && !pendingCancelNavigation}
          subtitle={t(translations.family_media.router_prompt_edit_title)}
          message={t(translations.family_media.router_prompt_edit_message)}
          onOK={() => !isSavingDescription && !isDeleting}
          onCancel={() => false}
        />
      )}

      {visibleDeleteTarget && (
        <DeleteResourceConfirmationModal
          open
          title={t(translations.family_media.delete_confirm_title)}
          description={t(translations.family_media.delete_confirm_description)}
          onCancel={() => {
            if (!isDeleting) setDeleteTarget(undefined);
          }}
          onConfirm={handleDeleteMedia}
          isDeleting={isDeleting}
          cancel_label={t(translations.family_media.cancel)}
          confirm_label={t(translations.family_media.delete)}
        />
      )}
    </ContentPage>
  );
}
